// src/hooks/useUsers.jsx
import { useState, useEffect, useCallback } from 'react';
import { usersService } from '../api/users';
import { useAuth } from './useAuth';

export const useUsers = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { user, isSupervisor, isManager } = useAuth();

  // Cargar usuarios al montar el hook
  useEffect(() => {
    if (isSupervisor || isManager) {
      fetchUsers();
    } else {
      setError(new Error('No tienes permisos para gestionar usuarios'));
      setLoading(false);
    }
  }, [isSupervisor, isManager]);

  const fetchUsers = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      //console.log('👤 Usuario actual:', user?.role);
      //console.log('🔑 Permisos:', { isSupervisor, isManager });
      
      const usersData = await usersService.getUsers();
      //console.log('✅ Usuarios obtenidos:', usersData);
      
      setUsers(usersData);
    } catch (err) {
      //console.error('❌ Error obteniendo usuarios:', err);
      setError(err);
    } finally {
      setLoading(false);
    }
  }, [user, isSupervisor, isManager]);

  // Crear usuario
  const createUser = async (userData) => {
    try {
      const newUser = await usersService.createUser(userData);
      setUsers(prev => [...prev, newUser]);
      return newUser;
    } catch (err) {
      console.error('Error creando usuario:', err);
      throw err;
    }
  };

  // Actualizar usuario 
  const updateUser = useCallback(async (userId, userData) => {
    setLoading(true);
    setError(null);
    try {
        const updatedUser = await usersService.updateUser(userId, userData);
	// Actualizar la lista de usuarios en el estado
	setUsers(prevUsers => prevUsers.map(user =>
		user.id === userId ? { ...user, ...updatedUser } : user
	));
	return updatedUser;
    } catch (err) {
      //console.error('Error actualizando usuario:', err);
	setError(err);
      	throw err;
    } finally {
	setLoading(false);
    }	
  }, []);

  // Eliminar usuario
  const deleteUser = async (userId) => {
    try {
      await usersService.deleteUser(userId);
      setUsers(prev => prev.filter(user => user.id !== userId));
    } catch (err) {
      //console.error('Error eliminando usuario:', err);
      throw err;
    }
  };

  const updateUserRole = async (userId, newRole) => {
    try {
      const updatedUser = await usersService.updateUser(userId, { role: newRole });
      setUsers(prev => prev.map(user => 
        user.id === userId ? { ...user, role: newRole } : user
      ));
      return updatedUser;
    } catch (err) {
      console.error('Error actualizando rol:', err);
      throw err;
    }
  };

  const updateUserValorHora = async (userId, valorHora) => {
    try {
      const updatedUser = await usersService.updateUser(userId, { valor_hora: valorHora });
      setUsers(prev => prev.map(user => 
        user.id === userId ? { ...user, valor_hora: valorHora } : user
      ));
      return updatedUser;
    } catch (err) {
      console.error('Error actualizando valor hora:', err);
      throw err;
    }
  };

  return {
    users,
    loading,
    error,
    fetchUsers,
    createUser,
    updateUser,
    deleteUser,
    updateUserRole,
    updateUserValorHora,
  };
};
